import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;

const getMetaData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/metadataConfig/${instanceId}?apiName=dataMgt`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `Data Management`,
      message: `Something went wrong in getmetadataConfigAPI => ${error}`,
    });
    logMessage(
      `dataMgt`,
      `Something went wrong in getmetadataConfigAPI API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getmetadataConfigAPI api endpoint");
  }
};

export const getMetaDataMgt = async (
  instanceId: string,
  tableName: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/metadataManagement/${instanceId}/${tableName}`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `Data Management`,
      message: `Something went wrong in getmetadataManagement API => ${error}`,
    });
    logMessage(
      `dataMgt`,
      `Something went wrong in getmetadataManagement API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getmetadataManagement api endpoint");
  }
};

export interface ListDataPayload {
  isDelete?: boolean;
  isEdit?: boolean;
  tableName: string;
  payload: Record<string, unknown>;
}

export const createOrUpdateListData = createAsyncThunk<
  string,
  ListDataPayload,
  { state: RootState }
>(
  "dataMgt/createOrUpdateListData",
  async (listDataPayload: ListDataPayload, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    const method = listDataPayload?.isDelete
      ? "DELETE"
      : listDataPayload?.isEdit
        ? "PUT"
        : "POST";
    try {
      await awsClientGateway({
        pathTemplate: `/metadataManagement/${instanceId}/${listDataPayload.tableName}`,
        method: method,
        body: { ...listDataPayload.payload },
      });
      return listDataPayload?.isDelete ? "delete" : "createOrUpdate";
    } catch (error) {
      clientLog({
        component: `Data Management`,
        message: `Something went wrong in createOrUpdatemetadataManagementListData API => ${error}`,
      });
      logMessage(
        `Data Management`,
        `Something went wrong in createOrUpdatemetadataManagementListData API => ${error}`,
        `error`,
      );
      return rejectWithValue(
        "Error in createOrUpdatemetadataManagementListData api endpoint",
      );
    }
  },
);

export const getTableListData = createAsyncThunk<
  { tableListData: AxiosResponse | unknown },
  string,
  { state: RootState }
>(
  "dataMgt/getTableListData",
  async (tableName: string, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    let tableListData: AxiosResponse | unknown = [];
    if (tableName) {
      tableListData = await getMetaDataMgt(
        instanceId,
        tableName,
        rejectWithValue,
      );
    }
    return { tableListData };
  },
);

export const getTableMetaData = createAsyncThunk<
  { headerData: AxiosResponse | unknown },
  void,
  { state: RootState }
>("dataMgt/getTableMetaData", async (_args, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  const headerData = await getMetaData(instanceId, rejectWithValue);
  return { headerData };
});

export const getResetDataManagement = createAction(
  "dataMgt/getResetDataManagement",
);
